import axios from 'axios';
import getApiInstance from '../common/apiInstance';
import { getApiErrorMessage } from '../common/apiErrorMessage';

const openingsApi = getApiInstance('/openings', 'no-settings');

export type OpeningListItem = {
  eco: string;
  slug: string;
  opening: string;
  familySlug: string;
  variationSlug: string;
};

export type OpeningFamilyRecord = {
  familyId: number;
  name: string;
  slug: string;
};

export type OpeningLineRecord = {
  lineId: number;
  familyId: number;
  eco: string;
  opening: string;
  familySlug: string;
  variationSlug: string;
  familyName: string;
  pgn?: string;
};

export type OpeningDetail = OpeningListItem & {
  pgn: string;
  moves: string[];
};

export type OpeningsListResponse = {
  openings: OpeningListItem[];
  total: number;
};

export type CoveredOpeningFamily = {
  familyId: number;
  name: string;
  slug: string;
  lineCount: number;
  gameCount: number;
};

export type CoveredFamiliesResponse = {
  families: CoveredOpeningFamily[];
  total: number;
};

export type CoveredFamilyLinesResponse = {
  family: CoveredOpeningFamily;
  openings: OpeningListItem[];
};

export const fetchOpenings = async (params?: {
  limit?: number;
  offset?: number;
}): Promise<OpeningsListResponse> => {
  try {
    const { data } = await openingsApi.get<OpeningsListResponse>('', {
      params: {
        limit: params?.limit,
        offset: params?.offset,
      },
    });
    return data;
  } catch (error) {
    throw new Error(
      getApiErrorMessage(error, { action: 'Failed to load openings' }),
    );
  }
};

export const fetchCoveredOpeningFamilies = async (params?: {
  limit?: number;
  offset?: number;
}): Promise<CoveredFamiliesResponse> => {
  try {
    const { data } = await openingsApi.get<CoveredFamiliesResponse>(
      '/families',
      {
        params: {
          limit: params?.limit,
          offset: params?.offset,
        },
      },
    );
    return data;
  } catch (error) {
    throw new Error(
      getApiErrorMessage(error, { action: 'Failed to load opening families' }),
    );
  }
};

export const fetchCoveredFamilyOpenings = async (
  familySlug: string,
): Promise<CoveredFamilyLinesResponse | null> => {
  try {
    const { data } = await openingsApi.get<CoveredFamilyLinesResponse>(
      `/families/by-slug/${encodeURIComponent(familySlug)}/lines`,
    );
    return data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null;
    }
    throw new Error(
      getApiErrorMessage(error, {
        action: 'Failed to load opening family lines',
      }),
    );
  }
};

export const fetchOpeningByEcoSlug = async (
  eco: string,
  slug: string,
): Promise<OpeningDetail | null> => {
  try {
    const { data } = await openingsApi.get<OpeningDetail>(
      `/${encodeURIComponent(eco)}/${encodeURIComponent(slug)}`,
    );
    return data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null;
    }
    throw new Error(
      getApiErrorMessage(error, { action: 'Failed to load opening' }),
    );
  }
};

export const fetchOpeningFamilyBySlug = async (
  familySlug: string,
): Promise<OpeningFamilyRecord | null> => {
  try {
    const { data } = await openingsApi.get<OpeningFamilyRecord>(
      `/families/by-slug/${encodeURIComponent(familySlug)}`,
    );
    return data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null;
    }
    throw new Error(
      getApiErrorMessage(error, { action: 'Failed to load opening family' }),
    );
  }
};

export const fetchOpeningLineBySlugs = async (
  familySlug: string,
  variationSlug: string,
  eco?: string,
): Promise<OpeningLineRecord | null> => {
  try {
    const { data } = await openingsApi.get<OpeningLineRecord>(
      `/families/by-slug/${encodeURIComponent(familySlug)}/lines/by-slug/${encodeURIComponent(variationSlug)}`,
      {
        params: eco ? { eco } : undefined,
      },
    );
    return data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null;
    }
    throw new Error(
      getApiErrorMessage(error, { action: 'Failed to load opening line' }),
    );
  }
};
